/**
 * List-item container resolution for utm_term (v1.10.0, positionContainerSelector)
 *
 * Before this module, utm_term numbered TAGGED LINKS sequentially (see
 * src/UtmLinker.js's `this.position` instance counter) — on a listicle
 * page the numbers drift away from the list-item RANK the moment an item
 * carries more than one link (a textlink + a button, say) or an intro link
 * precedes the list. When an admin configures `positionContainerSelector`
 * (a CSS selector matching each list-item container), utm_term instead
 * reflects the 1-based rank of the container a link falls inside or after —
 * every link belonging to the same item shares one number, by design (see
 * findItemPosition()'s own docblock).
 *
 * Deliberately pure/DOM-position-only: NO layout reads
 * (getBoundingClientRect etc.) — Node.compareDocumentPosition() is a tree-
 * order query, cheap and safe to run pre-paint, and it works identically on
 * a MutationObserver-triggered rescan of dynamically-inserted content.
 */

// Real DOM bitmask constants (Node.DOCUMENT_POSITION_FOLLOWING /
// DOCUMENT_POSITION_CONTAINED_BY per the DOM Standard). Hardcoded as
// literals rather than read off a global `Node` constructor — every real
// browser defines these identically and stably, and this repo's own test
// suite runs the DOM-dependent modules under plain `node --test` with a
// hand-rolled document/element stub (no jsdom, no global `Node`) — see
// tests/position-container-selector.test.mjs's own header comment for the
// established pattern every other DOM-dependent module here follows.
const DOCUMENT_POSITION_FOLLOWING = 4;
const DOCUMENT_POSITION_CONTAINED_BY = 16;

/**
 * Resolve the current pass's list-item containers, or `null` when legacy
 * per-link monotonic numbering should be used instead. Three cases all
 * collapse to `null` — the caller (UtmLinker.js) only ever needs to know
 * "container mode is off, use the counter":
 *  - `selector` is empty/whitespace-only (the default — feature off).
 *  - `selector` matches ZERO elements on this page (not a listicle; keep
 *    place/article pages byte-identical to their pre-1.10.0 behavior).
 *  - `selector` is syntactically invalid CSS (admin free text can throw in
 *    `querySelectorAll` — same guard philosophy as the v1.4.1 tocSelectors
 *    fix: a bad admin-configured selector must never break tagging).
 *
 * MUST be called ONCE PER processLinks() PASS, never cached across passes —
 * a MutationObserver rescan can see content that grew since the last pass
 * (a new list item appended via AJAX/infinite-scroll), so the container
 * list has to be re-queried fresh every time (see UtmLinker.js's call site).
 *
 * @param {string|undefined} selector Admin-configured CSS selector
 *   (settings.positionContainerSelector).
 * @return {Array|null} Matched elements in document order (a real Array,
 *   not a live NodeList), or null (legacy mode).
 */
export function resolvePositionContainers(selector) {
    if (typeof selector !== 'string' || selector.trim() === '') {
        return null;
    }

    try {
        const matches = document.querySelectorAll(selector);
        return matches && matches.length > 0 ? Array.from(matches) : null;
    } catch (e) {
        return null;
    }
}

/**
 * 1-based rank of `link` within `containers` (document order): the index
 * of the LAST container that precedes OR contains the link. `containers`
 * is assumed non-empty and in document order (exactly what
 * resolvePositionContainers() returns — it never returns an empty array,
 * only null).
 *
 * Multiple links inside the SAME container resolve to the SAME rank — this
 * is the point (utm_content already distinguishes a textlink from a button
 * within one item), not a bug: two links in list item #3 both get
 * utm_term=3_position.
 *
 * Returns `null` for a link that precedes every container — an "intro"
 * link before the first list item has no list-item rank to report; the
 * caller (generateUtmParams()) omits utm_term entirely for it rather than
 * inventing a 0/blank value.
 *
 * Handles "whole-card-is-a-link" (the configured selector matches an `<a>`
 * DIRECTLY, so the container element IS the link, not an ancestor of it) —
 * see containerPrecedesOrContains()'s own docblock for why that needs an
 * explicit identity check rather than relying on compareDocumentPosition()
 * alone (pr-review-council MEDIUM finding).
 *
 * @param {Object} link The link element.
 * @param {Array} containers Non-empty array of container elements, in
 *   document order.
 * @return {number|null}
 */
export function findItemPosition(link, containers) {
    for (let i = containers.length - 1; i >= 0; i--) {
        if (containers[i] === link || containerPrecedesOrContains(containers[i], link)) {
            return i + 1;
        }
    }
    return null;
}

/**
 * Whether `container` precedes `link` in document order, or contains it.
 * Queries the relation via `container.compareDocumentPosition(link)` (not
 * the reverse) — a bitmask describing `link`'s position relative to
 * `container`.
 *
 * Does NOT by itself cover `container === link` (whole-card-is-a-link: the
 * selector matches the `<a>` element itself, not a wrapper around it) —
 * `Node.compareDocumentPosition()` called on the IDENTICAL node returns `0`
 * (no bits at all, per the DOM Standard's own self-comparison contract),
 * which this bitmask check reads as "unrelated", not "contained". findItemPosition()
 * above checks `containers[i] === link` BEFORE falling through to this
 * function specifically to cover that case — without it, a whole-card link
 * silently inherited the PREVIOUS container's rank (an earlier container
 * still legitimately reads as FOLLOWING relative to this one), or `null`
 * when it was the very first container with no earlier one to (wrongly)
 * match instead.
 *
 * @param {Object} container
 * @param {Object} link
 * @return {boolean}
 */
function containerPrecedesOrContains(container, link) {
    if (!container || typeof container.compareDocumentPosition !== 'function') {
        return false;
    }

    const relation = container.compareDocumentPosition(link);
    return (relation & (DOCUMENT_POSITION_FOLLOWING | DOCUMENT_POSITION_CONTAINED_BY)) !== 0;
}
